import { useMemo, useState } from 'react';
import { AssuranceKeyMini, Icon } from '@garnet/ui';
import { useHashRoute } from './router.js';
import { CRUMB, NAV, ROUTES, type Route } from './nav.js';
import { VERTICAL_CONFIG, VERTICALS, type Vertical } from './config/consoleConfig.js';
import { FixtureApi } from './data/FixtureApi.js';
import type { GarnetApi } from './data/types.js';
import { useAsync } from './useAsync.js';
import { StateBox } from '@garnet/ui';
import { Dashboard } from './screens/Dashboard.js';
import { Flow } from './screens/Flow.js';
import { SupplierRecord } from './screens/SupplierRecord.js';
import { Requirements } from './screens/Requirements.js';
import { DocumentInbox } from './screens/DocumentInbox.js';
import { Renewals } from './screens/Renewals.js';
import { Cases } from './screens/Cases.js';
import { Professional, ProfessionalDesk } from './screens/Professional.js';
import { TranslationServices } from './screens/TranslationServices.js';
import { Tpm } from './screens/Tpm.js';
import { Settings } from './screens/Settings.js';
import { Advisor } from './screens/advisor/Advisor.js';

export function App({ api = new FixtureApi() as GarnetApi }: { api?: GarnetApi } = {}) {
  const [route, nav] = useHashRoute<Route>(ROUTES, 'dash');
  const [vertical, setVertical] = useState<Vertical>('medical');
  const ctx = useMemo(() => api, [api]);
  const config = VERTICAL_CONFIG[vertical];

  const { data, loading, error } = useAsync(() => ctx.getVerticalData(vertical), [vertical]);

  function go(r: Route): void {
    nav(r);
  }

  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <div className="wm">
            <span className="gem" />
            Garnet
          </div>
          <div className="tag">procurement diligence · {config.display_name}</div>
        </div>
        <nav className="nav">
          {NAV.map((g) => (
            <div key={g.grp}>
              <div className="grp" style={g.items[0]?.advisor ? { color: 'var(--adv)' } : undefined}>
                {g.grp}
              </div>
              {g.items.map((it) => (
                <a
                  key={it.k}
                  className={(route === it.k ? 'active ' : '') + (it.primary ? 'primary' : '')}
                  onClick={() => go(it.k)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && go(it.k)}
                  style={route === it.k && it.advisor ? { borderColor: 'var(--adv-line)', background: 'var(--adv-bg)' } : undefined}
                >
                  <span className="ni" style={it.advisor ? { color: 'var(--adv)' } : undefined}>
                    <Icon name={it.icon} size={15} />
                  </span>
                  {it.label}
                  {it.badge && <span className="badge">{it.badge}</span>}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div className="foot">
          <AssuranceKeyMini />
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div className="crumb">
            <b>{(CRUMB[route] || '').split(' · ')[0]}</b>
            {CRUMB[route] && CRUMB[route].includes(' · ') ? ' · ' + CRUMB[route].split(' · ').slice(1).join(' · ') : ''}
          </div>
          <div className="topbar-right">
            {/* vertical switcher — drives @garnet/config CONFIG, proving PRD §38 */}
            <div className="vswitch" role="group" aria-label="Vertical">
              {VERTICALS.map((v) => (
                <button key={v} aria-pressed={vertical === v} onClick={() => setVertical(v)}>
                  {v}
                </button>
              ))}
            </div>
            <span className="orgchip" title={`identifier · ${config.identity.identifier_label}`}>
              <span className="od" />
              {config.identity.identifier_label}
            </span>
            <span className="orgchip">
              <span className="od" />
              {data?.copy.org ?? 'Lench Procurement · EU residency'}
            </span>
          </div>
        </div>

        <div className="content">
          {error ? (
            <StateBox kind="error" title="Could not load this org's data">
              {error}
            </StateBox>
          ) : loading || !data ? (
            <StateBox kind="loading" title="Loading the portfolio…" />
          ) : route === 'dash' ? (
            <Dashboard data={data} go={go} />
          ) : route === 'flow' ? (
            <Flow data={data} config={config} go={go} />
          ) : route === 'record' ? (
            <SupplierRecord data={data} go={go} />
          ) : route === 'requirements' ? (
            <Requirements data={data} go={go} />
          ) : route === 'inbox' ? (
            <DocumentInbox data={data} />
          ) : route === 'renewals' ? (
            <Renewals data={data} />
          ) : route === 'cases' ? (
            <Cases data={data} />
          ) : route === 'professional' ? (
            <Professional data={data} go={go} />
          ) : route === 'translation' ? (
            <TranslationServices data={data} />
          ) : route === 'tpm' ? (
            <Tpm data={data} />
          ) : route === 'prodesk' ? (
            <ProfessionalDesk data={data} />
          ) : route === 'settings' ? (
            <Settings data={data} />
          ) : route === 'advisor' ? (
            <Advisor api={ctx} config={config} go={go} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
